import { useState, useEffect } from 'react';

export interface AuthState {
  user: { id: string; username: string; role: string } | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<{ id: string; username: string; role: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then((res) => {
        if (res.status === 401) {
          setUser(null);
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data) setUser({ id: data.id, username: data.username, role: data.role });
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error ?? 'Login failed');
    }

    const data = await res.json();
    setUser({ id: data.user.id, username: data.user.username, role: data.user.role });
  };

  const logout = async (): Promise<void> => {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
    setUser(null);
  };

  // Intercept 401s from protected endpoints by patching fetch globally once
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Only clear state on 401 for non-auth endpoints
      if (response.status === 401) {
        const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : '';
        const isAuthEndpoint = url.includes('/api/auth/');
        if (!isAuthEndpoint) {
          setUser(null);
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  return { user, loading, login, logout };
}

export default useAuth;
